// "Same in every language": a customer text kept as one value under SAME.
// Mirrors i18n.Same in internal/i18n/missing.go.

type Text = Record<string, string>

/** BCP 47 "mul": the key a text's one value for every language is kept under. */
export const SAME = 'mul'

/** Whether a text is one value for every language. */
export function isSame(text: Text | undefined | null): boolean {
  return !!text && Object.prototype.hasOwnProperty.call(text, SAME)
}

/** One value for every language, copied under the default language for older readers. */
export function sameText(value: string, defaultLang: string): Text {
  return value.trim() && defaultLang && defaultLang !== SAME ? { [SAME]: value, [defaultLang]: value } : { [SAME]: value }
}

/** What an editor shows for lang: the one value when same, else lang's own. */
export function ownWords(text: Text | undefined | null, lang: string): string {
  return (isSame(text) ? text![SAME] : text?.[lang]) ?? ''
}

/** The text with lang's words set; a same text takes them as its one value. */
export function withWords(text: Text | undefined | null, lang: string, value: string, defaultLang: string): Text {
  return isSame(text) ? sameText(value, defaultLang) : { ...text, [lang]: value }
}

/** Switched on, the default language's words become the one value; off, they return to it alone. */
export function switchSame(text: Text | undefined | null, on: boolean, defaultLang: string): Text {
  if (on) return sameText(text?.[defaultLang] || Object.values(text ?? {}).find((v) => v.trim()) || '', defaultLang)
  const value = text?.[SAME] ?? ''
  return value.trim() ? { [defaultLang]: value } : {}
}
